/**
 * Grundgerüst: Sidebar, mobiles Menü, Inhaltsbereich, Toast.
 *
 * Die Navigation zeigt nur freigeschaltete Bereiche. Eine Gruppe ohne
 * sichtbaren Punkt verschwindet samt Überschrift.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Wortmarke } from './Wortmarke';
import {
  IconAbmelden,
  IconAdmin,
  IconBeleg,
  IconBurger,
  IconGluehbirne,
  IconEinstellungen,
  IconAuftrag,
  IconFinanzen,
  IconHaken,
  IconKunden,
  IconProfil,
  IconSchloss,
  IconTag,
  IconVertrieb,
} from './Icons';

interface NavPunkt {
  key: string;
  pfad: string;
  label: ReactNode;
  titel: string;
  icon: ReactNode;
  /** Bereichsschlüssel; null bedeutet: immer sichtbar */
  bereich: string | null;
  schloss?: boolean;
}

interface NavGruppe {
  titel: string;
  punkte: NavPunkt[];
}

const GRUPPEN: NavGruppe[] = [
  {
    titel: '',
    punkte: [
      {
        key: 'start',
        pfad: '/',
        titel: 'Mein Tag',
        // Das "i" trägt die Akzentfarbe – kleines Markendetail aus dem Design.
        // aria-hidden auf der zerlegten Schreibweise, damit Screenreader nicht
        // "Me i n Tag" vorlesen; der lesbare Name steht im aria-label des Links.
        label: (
          <span aria-hidden="true">
            Me<span style={{ color: 'var(--eh-rot)' }}>i</span>n Tag
          </span>
        ),
        icon: <IconTag />,
        bereich: null,
      },
      {
        key: 'monitor',
        pfad: '/cockpit',
        titel: 'Vertriebscockpit',
        label: 'Vertriebscockpit',
        icon: <IconVertrieb />,
        bereich: 'monitor',
      },
    ],
  },
  {
    titel: 'Kunden',
    punkte: [
      {
        key: 'kunden',
        pfad: '/kunden',
        titel: 'Kundenstammverwaltung',
        label: 'Kundenstammverwaltung',
        icon: <IconKunden />,
        bereich: 'kunden',
      },
    ],
  },
  {
    /*
     * Eigene Gruppe, bewusst nicht bei den Auswertungen: Belege vorbereiten ist
     * Arbeit, keine Zahl zum Ansehen. Mit Schloss, weil dort Beträge liegen.
     */
    titel: 'Buchhaltung',
    punkte: [
      {
        key: 'buchhaltung',
        pfad: '/buchhaltung',
        titel: 'Buchhaltungsvorbereitung',
        label: 'Buchhaltungsvorbereitung',
        icon: <IconBeleg />,
        bereich: 'buchhaltung',
        schloss: true,
      },
    ],
  },
  {
    titel: 'Auswertungen',
    punkte: [
      {
        key: 'auftraege',
        pfad: '/auftraege',
        titel: 'Auftragsübersicht',
        label: 'Auftragsübersicht',
        icon: <IconAuftrag />,
        bereich: 'auftraege',
        schloss: true,
      },
      {
        key: 'finanzen',
        pfad: '/finanzen',
        titel: 'Finanzen',
        label: 'Finanzen',
        icon: <IconFinanzen />,
        bereich: 'finanzen',
        schloss: true,
      },
    ],
  },
];

export function Shell({ children }: { children: ReactNode }) {
  const { nutzer, darf, menuOffen, menuUmschalten, menuSchliessen } = useKora();
  const navigate = useNavigate();
  const ort = useLocation();

  useEffect(() => {
    menuSchliessen();
    // Beim Seitenwechsel schließt sich das mobile Menü.
  }, [ort.pathname, menuSchliessen]);

  const gruppen = GRUPPEN.map((g) => ({
    ...g,
    punkte: g.punkte.filter((p) => p.bereich === null || darf(p.bereich)),
  })).filter((g) => g.punkte.length > 0);

  const darfAnlegen = darf('monitor') || darf('kunden');

  return (
    <div className="eh-shell">
      <button className="eh-burger" onClick={menuUmschalten} title="Navigation" type="button">
        <IconBurger />
      </button>
      <div
        className={`eh-scrim ${menuOffen ? 'eh-scrim--an' : ''}`}
        onClick={menuSchliessen}
        aria-hidden="true"
      />

      <aside className={`eh-side ${menuOffen ? 'eh-side--offen' : ''}`}>
        <div className="eh-brand">
          <div className="eh-brand-mini">K</div>
          <div className="eh-brand-full">
            <img src="/eichenherz-logo.png" alt="Eichenherz" className="eh-brand-logo" />
            <div className="eh-wortmarke"><Wortmarke /></div>
          </div>
        </div>

        {darfAnlegen && (
          <button
            type="button"
            className="eh-knopf-primaer"
            title="Neues Angebot"
            onClick={() => navigate('/neu')}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            <span className="eh-navtext">Neues Angebot</span>
          </button>
        )}

        <nav className="eh-nav">
          {gruppen.map((gruppe) => (
            <div className="eh-nav-gruppe" key={gruppe.titel || 'haupt'}>
              {gruppe.titel && <div className="eh-nav-titel eh-navtext">{gruppe.titel}</div>}
              {gruppe.punkte.map((punkt) => (
                <NavLink
                  key={punkt.key}
                  to={punkt.pfad}
                  end={punkt.pfad === '/'}
                  title={punkt.titel}
                  aria-label={punkt.titel}
                  className={({ isActive }) =>
                    `eh-nav-punkt ${isActive ? 'eh-nav-punkt--aktiv' : ''}`
                  }
                >
                  <span className="eh-nav-icon">{punkt.icon}</span>
                  <span className="eh-nav-label eh-navtext">{punkt.label}</span>
                  {punkt.schloss && (
                    <span
                      className="eh-nav-schloss eh-navtext"
                      title="Sensibler Bereich – Zugriff nur für freigeschaltete Personen"
                    >
                      <IconSchloss />
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <Vorschlagspunkt />

        {nutzer && <Profilblock />}
      </aside>

      <main className="eh-main">{children}</main>

      <Toast />
    </div>
  );
}

/**
 * „Verbesserungsvorschläge" – abgesetzt über dem Profilblock.
 *
 * Bewusst nicht in den Arbeitsgruppen: Es ist eine Meta-Funktion zum Werkzeug
 * selbst, kein Arbeitsbereich. Sichtbar für alle, ohne Schloss.
 *
 * Der rote Zähler zeigt unbeantwortete Vorschläge und nur dem Administrator –
 * für alle anderen wäre er Dauerdekoration, weil sie nichts entscheiden können.
 * Er ist die Benachrichtigung im Werkzeug; die Mail geht zusätzlich hinaus.
 */
function Vorschlagspunkt() {
  const { nutzer } = useKora();

  const anzahl = useQuery({
    queryKey: ['vorschlaege-anzahl'],
    queryFn: () => api.hole<{ offen: number }>('/vorschlaege/anzahl'),
    enabled: !!nutzer?.istAdmin,
    staleTime: 60_000,
  });

  const offen = nutzer?.istAdmin ? (anzahl.data?.offen ?? 0) : 0;

  return (
    <div className="eh-nav-meta">
      <div className="eh-nav-titel eh-navtext">Kora</div>
      <NavLink
        to="/vorschlaege"
        title="Verbesserungsvorschläge"
        aria-label="Verbesserungsvorschläge"
        className={({ isActive }) => `eh-nav-punkt ${isActive ? 'eh-nav-punkt--aktiv' : ''}`}
      >
        <span className="eh-nav-icon">
          <IconGluehbirne />
        </span>
        <span className="eh-nav-label eh-navtext">Verbesserungsvorschläge</span>
        {offen > 0 && (
          <span
            className="eh-nav-zaehler eh-navtext"
            title={`${offen} unbeantwortete${offen === 1 ? 'r Vorschlag' : ' Vorschläge'}`}
          >
            {offen}
          </span>
        )}
      </NavLink>
    </div>
  );
}

function Profilblock() {
  const { nutzer, modalOeffnen, abmelden } = useKora();
  const [offen, setOffen] = useState(false);
  const block = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    function ausserhalb(e: MouseEvent) {
      if (block.current && !block.current.contains(e.target as Node)) setOffen(false);
    }
    document.addEventListener('mousedown', ausserhalb);
    return () => document.removeEventListener('mousedown', ausserhalb);
  }, [offen]);

  if (!nutzer) return null;

  return (
    <div className="eh-profilblock" ref={block}>
      {offen && (
        <div className="eh-profilmenue" role="menu">
          <button
            type="button"
            onClick={() => {
              setOffen(false);
              modalOeffnen({ art: 'profil' });
            }}
          >
            <span style={{ display: 'flex', color: 'var(--eh-text-4)', flex: 'none' }}>
              <IconProfil />
            </span>
            <span style={{ flex: 1 }}>Profil</span>
          </button>

          {nutzer.istAdmin && (
            <>
              <button
                type="button"
                onClick={() => {
                  setOffen(false);
                  modalOeffnen({ art: 'einstellungen' });
                }}
              >
                <span style={{ display: 'flex', color: 'var(--eh-text-4)', flex: 'none' }}>
                  <IconEinstellungen />
                </span>
                <span style={{ flex: 1 }}>Einstellungen</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setOffen(false);
                  modalOeffnen({ art: 'adminCenter' });
                }}
              >
                <span style={{ display: 'flex', color: 'var(--eh-text-4)', flex: 'none' }}>
                  <IconAdmin />
                </span>
                <span style={{ flex: 1 }}>Admin Center</span>
              </button>
            </>
          )}

          <div className="eh-profilmenue-trenner" />

          <button type="button" className="eh-abmelden" onClick={() => void abmelden()}>
            <span style={{ display: 'flex', color: 'var(--eh-rot)', flex: 'none' }}>
              <IconAbmelden />
            </span>
            <span style={{ flex: 1 }}>Abmelden</span>
          </button>
        </div>
      )}

      <button
        type="button"
        className={`eh-profilknopf ${offen ? 'eh-profilknopf--offen' : ''}`}
        title="Profil und Abmelden"
        onClick={() => setOffen((a) => !a)}
      >
        <span
          className="eh-avatar"
          style={{
            width: 34,
            height: 34,
            fontSize: 12.5,
            ...(nutzer.profilbild ? { backgroundImage: `url(${nutzer.profilbild})` } : {}),
          }}
        >
          {nutzer.profilbild ? '' : nutzer.initialen}
        </span>
        <span
          className="eh-navtext"
          style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, flex: 1, minWidth: 0 }}
        >
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>{nutzer.name}</span>
          <span
            style={{
              fontSize: 11.5,
              color: 'var(--eh-text-5)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nutzer.rolle}
          </span>
        </span>
        <span className="eh-navtext" style={{ fontSize: 11, color: 'var(--eh-text-6)', flex: 'none' }}>
          {offen ? '▾' : '▴'}
        </span>
      </button>
    </div>
  );
}

function Toast() {
  const { toast, toastSchliessen } = useKora();
  if (!toast) return null;
  return (
    <div className="eh-toast" role="status" onClick={toastSchliessen}>
      <IconHaken />
      <span>{toast}</span>
    </div>
  );
}
